import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { buildIdentityLogoutUrl } from '../lib/auth/identityLogout.ts';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { Select } from '../components/Field.tsx';
import { NavIcon, type IconName } from '../components/NavIcon.tsx';
import { useActiveStation, setActiveStation } from '../lib/activeStation.ts';
import { scopeSummaryLabel, visibleStations } from '../lib/adminAccess.ts';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import { useSessionTimeout } from '../lib/auth/useSessionTimeout.ts';
import { BRAND } from '../lib/brand.ts';
import { stationOptions } from '../lib/companyInfo.ts';
import { usePortalSettings } from '../lib/portalSettings.tsx';

// Accent palette for the admin shell chrome (matches --brand-primary).
const ACCENT = '#4D148C';
const ACCENT_LIGHT = '#F3EBFA';

type NavGroup = 'overview' | 'operate' | 'configure' | 'monitor' | 'admin';
type NavRow = {
  to: string;
  label: string;
  icon: IconName;
  group: NavGroup;
  end?: boolean;
};

const NAV: NavRow[] = [
  { to: '/admin', label: 'Dashboard', icon: 'dashboard', group: 'overview', end: true },
  { to: '/admin/companies', label: 'Vendors', icon: 'building', group: 'operate' },
  { to: '/admin/expiring-documents', label: 'Expiring documents', icon: 'inbox', group: 'operate' },
  { to: '/admin/questionnaires', label: 'Questionnaires', icon: 'list-checks', group: 'configure' },
  { to: '/admin/documents', label: 'Documents', icon: 'file-text', group: 'configure' },
  { to: '/admin/settings', label: 'Settings', icon: 'settings', group: 'admin' },
];

const GROUP_LABEL: Record<NavGroup, string> = {
  overview: 'Overview',
  operate: 'Operate',
  configure: 'Configure',
  monitor: 'Monitor',
  admin: 'Admin',
};

type GroupedRow =
  | { kind: 'header'; key: string; label: string }
  | { kind: 'item'; key: string; row: NavRow };

function groupNav(rows: NavRow[]): GroupedRow[] {
  const out: GroupedRow[] = [];
  let last: NavGroup | null = null;
  for (const r of rows) {
    if (r.group !== last) {
      out.push({ kind: 'header', key: `h-${r.group}`, label: GROUP_LABEL[r.group] });
      last = r.group;
    }
    out.push({ kind: 'item', key: r.to, row: r });
  }
  return out;
}

function initials(name: string | undefined): string {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  const first = parts[0] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase() || 'U';
  const last = parts[parts.length - 1] ?? '';
  return `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase() || 'U';
}

export function AdminShell() {
  const { user, loading, signOut, accessDenied } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useSessionTimeout({
    enabled: !!user,
    onExpire: () => {
      void signOut();
      navigate('/admin/login', { replace: true });
    },
  });

  // Deliberate sign-out, which is NOT the same as an expired session.
  //
  // Order matters: navigate away from AdminShell FIRST. signOut() awaits the
  // Supabase call before clearing state, so if we stayed mounted the `!user`
  // guard below would render <Navigate to="/admin/login">, and that page
  // auto-starts SSO — re-authenticating instantly against the still-live central
  // session. That is the bug this ordering prevents.
  //
  // Then hand off to the central sign-out route if one is configured. Supabase's
  // OAuth server has no end_session_endpoint, so without that hand-off the
  // central session survives and the next sign-in needs no credentials.
  const handleSignOut = useCallback(async () => {
    navigate('/admin/signed-out', { replace: true });
    await signOut();
    const centralLogout = buildIdentityLogoutUrl(
      import.meta.env.VITE_IDENTITY_LOGOUT_URL,
      `${window.location.origin}/admin/signed-out`,
    );
    if (centralLogout) window.location.href = centralLogout;
  }, [navigate, signOut]);

  const { settings, loading: settingsLoading } = usePortalSettings();
  const queryClient = useQueryClient();
  const activeStation = useActiveStation();
  const stations = visibleStations(user?.assigned_stations ?? null, stationOptions(settings));
  const stationsKey = stations.join(' ');

  // A stored station that's no longer visible (assignment revoked, station
  // renamed) silently resets to "All my stations". Waits for auth + settings
  // so the initial empty state doesn't wipe the persisted value on reload.
  useEffect(() => {
    if (loading || settingsLoading) return;
    if (activeStation && !stations.includes(activeStation)) setActiveStation(null);
    // stations is derived from stationsKey; listing stationsKey keeps deps stable.
  }, [activeStation, stationsKey, loading, settingsLoading]);

  if (loading) return <CenteredSpinner />;
  // Refused, not signed out. This MUST come before the `!user` guard: a refused
  // user has no `user` either, and /admin/login auto-starts SSO — so falling
  // through to that guard would send them back through the IdP, straight into
  // the same refusal, forever.
  if (accessDenied) {
    return <Navigate to="/admin/no-access" state={{ reason: accessDenied.reason }} replace />;
  }
  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  const rows = groupNav(NAV);

  function onActiveStationChange(value: string) {
    setActiveStation(value || null);
    // Every vendor surface is scoped server-side by the header — refetch all.
    void queryClient.invalidateQueries();
  }

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#F8FAFC' }}>
      {/* ===== Sidebar ===== */}
      <aside
        className="relative flex w-[260px] shrink-0 flex-col bg-white"
        style={{ borderRight: '1px solid #E2E8F0' }}
      >
        {/* Locate2u brand logo + portal sublabel */}
        <div className="px-5 pb-4 pt-5" style={{ borderBottom: '1px solid #F1F5F9' }}>
          <a href="https://www.locate2u.com" target="_blank" rel="noopener noreferrer">
            <img src="/locate2u-logo.png" alt="Locate2u" className="h-8 w-auto" />
          </a>
          <div className="mt-2 text-[13px]" style={{ color: '#64748B' }}>
            Admin Portal
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {rows.map((row) => {
            if (row.kind === 'header') {
              return (
                <div
                  key={row.key}
                  className="font-mono-eyebrow px-3 pb-1.5 pt-4 text-[9.5px] tracking-[0.14em]"
                  style={{ color: '#94A3B8' }}
                >
                  {row.label.toUpperCase()}
                </div>
              );
            }
            const item = row.row;
            return (
              <NavLink
                key={row.key}
                to={item.to}
                end={item.end ?? false}
                className={({ isActive }) =>
                  `relative flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-[15px] transition ${
                    isActive ? 'font-semibold' : 'font-medium hover:bg-slate-50'
                  }`
                }
                style={({ isActive }) =>
                  isActive ? { background: ACCENT_LIGHT, color: ACCENT } : { color: '#64748B' }
                }
              >
                {({ isActive }) => (
                  <>
                    <NavIcon
                      name={item.icon}
                      size={20}
                      style={{ color: isActive ? ACCENT : '#94A3B8' }}
                    />
                    <span className="flex-1 text-left">{item.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Active station switcher — narrows every vendor surface (screenshot spec) */}
        {stations.length > 0 && (
          <div className="px-4 pb-3 pt-4" style={{ borderTop: '1px solid #F1F5F9' }}>
            <div
              className="font-mono-eyebrow flex items-center gap-1.5 pb-2 text-[9.5px] tracking-[0.14em]"
              style={{ color: '#94A3B8' }}
            >
              <NavIcon name="building" size={12} style={{ color: '#94A3B8' }} />
              ACTIVE STATION
            </div>
            <Select
              value={activeStation ?? ''}
              onChange={(e) => onActiveStationChange(e.target.value)}
              aria-label="Active station"
            >
              <option value="">All my stations</option>
              {stations.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </div>
        )}

        {/* Footer: real user + sign out */}
        <div className="px-3 pb-4 pt-3" style={{ borderTop: '1px solid #F1F5F9' }}>
          <button
            type="button"
            onClick={() => void handleSignOut()}
            className="flex w-full items-center gap-3 rounded-lg p-2 text-left transition hover:bg-slate-50"
            title="Sign out"
          >
            <div
              className="font-mono-eyebrow flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[12px] font-bold"
              style={{ background: ACCENT_LIGHT, color: ACCENT }}
            >
              {initials(user.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-semibold" style={{ color: '#1E293B' }}>
                {user.name}
              </div>
              <div className="truncate text-[11px]" style={{ color: '#64748B' }}>
                {user.email}
              </div>
              <div className="truncate text-[11px]" style={{ color: '#94A3B8' }}>
                {user.role === 'admin' ? 'Admin' : 'Reviewer'} ·{' '}
                {scopeSummaryLabel(user.assigned_stations, activeStation)}
              </div>
            </div>
            <NavIcon name="log-out" size={16} style={{ color: '#94A3B8' }} />
          </button>
        </div>
      </aside>

      {/* ===== Main: top bar + content ===== */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top header bar: FedEx brand mark + "Powered by Locate2u" lockup */}
        <header
          className="flex items-center gap-3 bg-white px-8 py-4"
          style={{ borderBottom: '1px solid #E2E8F0' }}
        >
          <img src={BRAND.logoUrl} alt={BRAND.logoAlt} className="h-7 w-auto" />
          <span aria-hidden className="h-6 w-px bg-slate-200" />
          <span className="text-sm text-slate-500">
            Powered by{' '}
            <a
              href="https://www.locate2u.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium hover:underline"
              style={{ color: 'var(--brand-primary)' }}
            >
              Locate2u
            </a>
          </span>
        </header>

        <main className="flex-1 overflow-y-auto" style={{ background: '#F8FAFC' }}>
          <div className="px-8 py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
